// src/components/Consent.jsx
// src/components/Consent.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";

export default function Consent({ onComplete }) {
  const [user, setUser] = useState(null);
  const [requiredChecked, setRequiredChecked] = useState(false);
  const [researchChecked, setResearchChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // track auth
  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => setUser(u));
    return off;
  }, []);

  // if already consented, skip
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const ref = doc(db, "users", user.uid, "consents", "current");
        const snap = await getDoc(ref);
        if (snap.exists() && snap.data()?.accepted === true) {
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        console.warn("[Consent] pre-check failed:", e);
      }
    })();
  }, [user, navigate]);

  const onContinue = async () => {
    if (!user || !requiredChecked || saving) return;
    setSaving(true);
    try {
      const uid = user.uid;

      // Upsert current consent
      await setDoc(
        doc(db, "users", uid, "consents", "current"),
        {
          accepted: true,
          researchOptIn: !!researchChecked,
          platform: "web",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Append to history
      await addDoc(collection(db, "users", uid, "consentHistory"), {
        accepted: true,
        researchOptIn: !!researchChecked,
        platform: "web",
        createdAt: serverTimestamp(),
      });

      onComplete?.();
      navigate("/dashboard", { replace: true });

      // safety fallback
      setTimeout(() => {
        if (!window.location.pathname.includes("/dashboard")) {
          window.location.assign("/dashboard");
        }
      }, 300);
    } catch (err) {
      console.error("Consent save failed", err);
      alert(`Could not save consent: ${err.code || err.message}`);
      setSaving(false);
    }
  };

  return (
    <div className="consent-card" style={{ maxWidth: 520, margin: "48px auto" }}>
      <h2>Your Data, Your Control</h2>

      <p><strong>What we collect:</strong> BP, Glucose, HbA1c, Meds, Profile</p>
      <p><strong>How we protect:</strong> Encrypted storage, no ads</p>
      <p><strong>Your choices:</strong> You control consent & deletion</p>

      <label style={{ display: "block", marginTop: 16 }}>
        <input
          type="checkbox"
          checked={requiredChecked}
          onChange={(e) => setRequiredChecked(e.target.checked)}
        />{" "}
        I consent to WellSyncer securely storing my health data <em>(Required)</em>
      </label>

      <label style={{ display: "block", marginTop: 8 }}>
        <input
          type="checkbox"
          checked={researchChecked}
          onChange={(e) => setResearchChecked(e.target.checked)}
        />{" "}
        I allow my de-identified data to be used for research & app improvement{" "}
        <em>(Optional)</em>
      </label>

      <button
        onClick={onContinue}
        disabled={!requiredChecked || saving || !user}
        style={{
          marginTop: 20,
          opacity: !requiredChecked || saving || !user ? 0.6 : 1,
          padding: "10px 16px",
        }}
      >
        {saving ? "Saving..." : "Continue →"}
      </button>
    </div>
  );
}
